import apiClient from '../client.js'
import * as Sentry from '@sentry/vue'

export const ordersAPI = {
  // Public order endpoints
  async createOrder(orderData) {
    try {
      Sentry.logger.info('Creating order', {
        shipping_method_id: orderData.shipping_method_id,
        payment_method_id: orderData.payment_method_id,
        has_referral_code: !!orderData.referral_code,
        has_pickup_point: !!orderData.shipping_address?.pickup_point_id
      })
      
      const response = await apiClient.post('/orders', orderData)
      
      Sentry.logger.info('Order created successfully', {
        order_number: response.data?.order?.order_number,
        order_id: response.data?.order?.id,
        total: response.data?.order?.total
      })
      
      return response.data
    } catch (error) {
      Sentry.logger.error('Order creation failed', {
        error: error.response?.data?.message || error.message,
        status_code: error.response?.status,
        shipping_method_id: orderData.shipping_method_id,
        payment_method_id: orderData.payment_method_id
      })
      throw error
    }
  },

  async getOrder(orderNumber, email = null) {
    const params = email ? { email } : {}
    const response = await apiClient.get(`/orders/${orderNumber}`, { params })
    return response.data
  },

  async getInvoice(orderNumber, email = null) {
    const params = email ? { email } : {}
    const response = await apiClient.get(`/orders/${orderNumber}/invoice`, {
      params,
      responseType: 'blob'
    })
    return response.data
  },

  // Authenticated order endpoints
  async getOrders(params = {}) {
    const response = await apiClient.get('/orders', { params })
    return response.data
  },

  async recreateOrder(orderNumber) {
    try {
      Sentry.logger.info('Recreating order', {
        order_number: orderNumber
      })
      
      const response = await apiClient.post(`/orders/${orderNumber}/recreate`)
      
      Sentry.logger.info('Order recreated successfully', {
        order_number: orderNumber,
        new_cart_id: response.data?.cart?.id
      })
      
      return response.data
    } catch (error) {
      Sentry.logger.error('Order recreation failed', {
        order_number: orderNumber,
        error: error.response?.data?.message || error.message,
        status_code: error.response?.status
      })
      throw error
    }
  },

  // Admin order endpoints
  async getAdminOrders(params = {}) {
    const response = await apiClient.get('/admin/orders', { params })
    return response.data
  },

  async getOrderStats() {
    const response = await apiClient.get('/admin/orders/stats')
    return response.data
  },

  // Number of orders in each admin-v2 list view
  async getAdminOrderCounts() {
    const response = await apiClient.get('/admin/orders/counts')
    return response.data
  },

  async getOrderDailyStats(params = {}) {
    const response = await apiClient.get('/admin/orders/daily-stats', { params })
    return response.data
  },

  async getAdminOrder(orderId) {
    const response = await apiClient.get(`/admin/orders/${orderId}`)
    return response.data
  },

  async updateOrder(orderNumber, orderData) {
    const response = await apiClient.put(`/admin/orders/${orderNumber}`, orderData)
    return response.data
  },

  async deleteOrder(orderNumber, restock = false) {
    const response = await apiClient.delete(`/admin/orders/${orderNumber}`, { params: { restock: restock ? 1 : 0 } })
    return response.data
  },

  async updateOrderStatus(orderNumber, status) {
    const response = await apiClient.put(`/admin/orders/${orderNumber}/status`, { status })
    return response.data
  },

  async getAdminInvoice(orderNumber) {
    const response = await apiClient.get(`/admin/orders/${orderNumber}/invoice`, {
      responseType: 'blob'
    })
    return response.data
  },

  async createAdminOrder(orderData) {
    const response = await apiClient.post('/admin/orders', orderData)
    return response.data
  },

  async ephExportOrders(orderNumbers) {
    const response = await apiClient.post('/admin/eph/export', { order_numbers: orderNumbers })
    return response.data
  },

  async ephGetSheets() {
    const response = await apiClient.get('/admin/eph/sheets')
    return response.data
  },

  async ephGetSheetStatus(sheetId) {
    const response = await apiClient.get(`/admin/eph/sheets/${sheetId}/status`)
    return response.data
  },

  async ephRegisterSheet(sheetId) {
    const response = await apiClient.post(`/admin/eph/sheets/${sheetId}/register`)
    return response.data
  },

  async ephGetLabels(sheetId) {
    const response = await apiClient.get(`/admin/eph/sheets/${sheetId}/labels`)
    return response.data
  },

  async ephGetLabelsA4(sheetId) {
    const response = await apiClient.get(`/admin/eph/sheets/${sheetId}/labels/a4`, {
      responseType: 'blob'
    })
    return response.data
  },

  async ephDeleteSheet(sheetId) {
    const response = await apiClient.delete(`/admin/eph/sheets/${sheetId}`)
    return response.data
  }
}
